import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    Box,
    Button,
    CircularProgress,
    Container,
    Fade,
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    Link,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../axios/axiosHandler';
import { AppRoutes, EndPoints } from '../axios/endPoints';
import { enqueueSnackbar } from '../store/app-slice/app';
import { useAppDispatch } from '../store/store';
import { handleNotificationVariant } from './notifications/_common';
import type { BasicResponse, RegisterModel } from '../types/auth.types';
import { usePhoneCountries } from '../utils/usePhoneCountries';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { PhoneCountryOptionRow } from './common/PhoneCountryOptionRow';

interface RegisterForm {
    firstName: string;
    lastName: string;
    companyName: string;
    jobTitle: string;
    phoneCountryCode: string;
    phoneNationalNumber: string;
    email: string;
}

const schema = (t: Function) =>
    yup.object().shape({
        firstName: yup.string().required(t('REGISTER_FIRST_NAME_REQUIRED')),
        lastName: yup.string().required(t('REGISTER_LAST_NAME_REQUIRED')),
        companyName: yup.string().required(t('REGISTER_COMPANY_NAME_REQUIRED')),
        jobTitle: yup.string().required(t('REGISTER_JOB_TITLE_REQUIRED')),
        email: yup
            .string()
            .email(t('REGISTER_EMAIL_VALIDATION'))
            .required(t('REGISTER_EMAIL_REQUIRED')),
        phoneCountryCode: yup.string().required(t('REGISTER_PHONE_PREFIX_REQUIRED')),
        phoneNationalNumber: yup
            .string()
            .required(t('REGISTER_PHONE_NUMBER_REQUIRED'))
            .matches(/^[0-9\s()-]{5,}$/i, t('REGISTER_PHONE_NUMBER_VALIDATION')),
    });

const defaultValues: RegisterForm = {
    firstName: '',
    lastName: '',
    companyName: '',
    jobTitle: '',
    phoneCountryCode: 'IT',
    phoneNationalNumber: '',
    email: '',
};

export default function Register() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { countries } = usePhoneCountries();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const { control, handleSubmit, formState: { errors } } = useForm<RegisterForm>({
        mode: 'onBlur',
        resolver: yupResolver(schema(t)),
        defaultValues,
    });

    const countryOptions = useMemo(() => {
        const items = (countries ?? []).map((c) => {
            const translated = t(c.title);
            const label = translated === c.title ? c.code : translated;
            return {
                ...c,
                label: `${label}`.trim(),
            };
        });
        items.sort((a, b) => a.label.localeCompare(b.label));
        return items;
    }, [countries, t]);

    const { executeRecaptcha } = useGoogleReCaptcha();

    const onSubmit = async (data: RegisterForm) => {
        if (!executeRecaptcha) {
            dispatch(
                enqueueSnackbar({
                    message: t('BE_USERMGMT_CAPTCHA_NOT_LOADED'),
                    options: { variant: 'error', persist: true },
                })
            );
            return;
        }

        setIsSubmitting(true);

        try {
            const recaptchaToken = await executeRecaptcha('register');

            if (!recaptchaToken || recaptchaToken.trim() === "") {
                dispatch(
                    enqueueSnackbar({
                        message: t('BE_USERMGMT_CAPTCHA_BLOCKED'),
                        options: { variant: 'error', persist: true, style: { whiteSpace: "pre-line" } },
                    })
                );
                return;
            }

            const changePasswordUrl = new URL(
                AppRoutes.RESET_PASSWORD,
                window.location.protocol + '//' + window.location.host
            ).href;

            const selected = countryOptions.find((c) => c.code === data.phoneCountryCode);
            const dialCode = selected?.dialCode ?? '';
            const phoneNumber = [dialCode, data.phoneNationalNumber.trim()].filter(Boolean).join(' ').trim();

            const payload: RegisterModel = {
                firstName: data.firstName,
                lastName: data.lastName,
                companyName: data.companyName,
                jobTitle: data.jobTitle,
                phoneNumber,
                email: data.email,
                changePasswordUrl,
                captchaToken: recaptchaToken,
            };

            const response = await axiosInstance.post<BasicResponse>(EndPoints.REGISTER, payload);
            const res = response.data;

            dispatch(
                enqueueSnackbar({
                    message: t(`${res.message}`),
                    options: {
                        variant: handleNotificationVariant(res.success),
                        persist: !res.success,
                    },
                })
            );
            if (res.success) {
                setIsSubmitted(true);
            }
        } catch (e: any) {
            const message = e?.response?.data?.message ?? 'BE_GENERIC_ERROR';
            dispatch(
                enqueueSnackbar({
                    message: t(`${message}`),
                    options: { variant: 'error', persist: true },
                })
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Container maxWidth="sm" sx={{ p: 2, px: { xs: 2, sm: 4 } }}>
            <Fade in={!isSubmitted} unmountOnExit>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="h5" fontWeight="bold" mb={1}>
                    {t('REGISTER_TITLE')}
                </Typography>

                <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 1, width: '100%' }} noValidate>
                    <Controller
                        control={control}
                        name="firstName"
                        render={({ field, fieldState: { error } }) => (
                            <TextField
                                {...field}
                                margin="normal"
                                fullWidth
                                variant="outlined"
                                label={t('REGISTER_FIRST_NAME_LABEL')}
                                error={!!error}
                                helperText={error ? errors.firstName?.message : ''}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="lastName"
                        render={({ field, fieldState: { error } }) => (
                            <TextField
                                {...field}
                                margin="normal"
                                fullWidth
                                variant="outlined"
                                label={t('REGISTER_LAST_NAME_LABEL')}
                                error={!!error}
                                helperText={error ? errors.lastName?.message : ''}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="companyName"
                        render={({ field, fieldState: { error } }) => (
                            <TextField
                                {...field}
                                margin="normal"
                                fullWidth
                                variant="outlined"
                                label={t('REGISTER_COMPANY_NAME_LABEL')}
                                error={!!error}
                                helperText={error ? errors.companyName?.message : ''}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="jobTitle"
                        render={({ field, fieldState: { error } }) => (
                            <TextField
                                {...field}
                                margin="normal"
                                fullWidth
                                variant="outlined"
                                label={t('REGISTER_JOB_TITLE_LABEL')}
                                error={!!error}
                                helperText={error ? errors.jobTitle?.message : ''}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="email"
                        render={({ field, fieldState: { error } }) => (
                            <TextField
                                {...field}
                                margin="normal"
                                fullWidth
                                variant="outlined"
                                label={t('REGISTER_EMAIL_LABEL')}
                                type="email"
                                error={!!error}
                                helperText={error ? errors.email?.message : ''}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                            />
                        )}
                    />

                    <Grid container spacing={2} alignItems="flex-start" sx={{ mt: 0 }}>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                control={control}
                                name="phoneCountryCode"
                                render={({ field, fieldState: { error } }) => (
                                    <FormControl fullWidth size="small" margin="dense" error={!!error}>
                                        <InputLabel id="register-phone-prefix">{t('REGISTER_PHONE_PREFIX_LABEL')}</InputLabel>
                                        <Select
                                            {...field}
                                            labelId="register-phone-prefix"
                                            label={t('REGISTER_PHONE_PREFIX_LABEL')}
                                            sx={{
                                                '& .MuiSelect-select': {
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    width: '100%',
                                                    height: '100%',
                                                },
                                            }}
                                            renderValue={(selected) => {
                                                const selectedCode = String(selected ?? '');
                                                const c = countryOptions.find((x) => x.code === selectedCode);
                                                if (!c) return selectedCode;

                                                return <PhoneCountryOptionRow country={c} variant="menu" />;
                                            }}
                                        >
                                            {countryOptions.map((c) => (
                                                <MenuItem key={c.code} value={c.code}>
                                                    <PhoneCountryOptionRow country={c} variant="menu" />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>
                                            {error ? errors.phoneCountryCode?.message : ''}
                                        </FormHelperText>
                                    </FormControl>
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                control={control}
                                name="phoneNationalNumber"
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        size="small"
                                        margin="dense"
                                        variant="outlined"
                                        label={t('REGISTER_PHONE_NUMBER_LABEL')}
                                        type="tel"
                                        error={!!error}
                                        helperText={error ? errors.phoneNationalNumber?.message : ''}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>

                    <Button
                        className="button-color"
                        disabled={isSubmitting}
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2 }}
                    >
                        {isSubmitting ? <CircularProgress size={24} sx={{ color: 'white' }} /> : t('REGISTER_SUBMIT')}
                    </Button>

                    <Grid container justifyContent="flex-end">
                        <Grid item>
                            <Link className="button-text-color" href={AppRoutes.LOG_IN} variant="body2">
                                {t('REGISTER_BACK_TO_LOGIN')}
                            </Link>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </Fade>
        <Fade in={isSubmitted} unmountOnExit>
           {successfulRegister(t)}
        </Fade>
        </Container>
    );
}

const successfulRegister = (t: any) => (
<Box
  sx={{
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: 1.5,
    padding: 2,
  }}
>
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1, 
    }}
  >
    <Typography variant="h5" fontWeight="bold">
      {t("REGISTER_SUCCESSFUL_MESSAGE")}
    </Typography>

    <CheckCircleIcon color="success" />
  </Box>

  <Typography>
    {t("REGISTER_SUCCESSFUL_INSTRUCTIONS")}
  </Typography>

</Box>

)
